/**
 * Briefvorlagen für Zahlungsaufforderungen (2026-02).
 *
 * Route: /briefvorlagen/zahlungsaufforderungen (Admin/Manager).
 *
 * Layout:
 *  - Links: Situationszuordnungs-Matrix (Miet-Szenarien × Stufe 1-4 + Kaution).
 *  - Rechts: Vorlagen-Editor (Name, Betreff, HTML-Body, verfügbare Platzhalter).
 *
 * Systemvorlagen sind mit einem Schloss markiert — sie können bearbeitet und
 * „Auf Standard zurücksetzen" werden, aber nicht gelöscht. Benutzerdefinierte
 * Vorlagen können gelöscht werden; ist die Vorlage einer Situation zugewiesen,
 * warnt Domivus vor dem Löschen und bietet an, die Zuordnung auf die
 * System-Standard-Vorlage zurückzusetzen.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Envelope, Lock, ArrowClockwise, Trash, FloppyDisk, Plus, Copy, Info,
} from "@phosphor-icons/react";


export default function ZahlungsaufforderungsVorlagen() {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [situations, setSituations] = useState({ rent: [], deposit: [], levels: [] });
  const [placeholders, setPlaceholders] = useState({ groups: [] });
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [{ data: list }, { data: sit }, { data: ph }] = await Promise.all([
      api.get("/dunning/letter-templates"),
      api.get("/dunning/letter-templates/situations"),
      api.get("/dunning/letter-templates/placeholders"),
    ]);
    setTemplates(list.templates || []);
    setAssignments(list.assignments || []);
    setSituations(sit);
    setPlaceholders(ph);
    if (!activeId && (list.templates || []).length) {
      setActiveId(list.templates[0].id);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const active = useMemo(
    () => templates.find((t) => t.id === activeId) || null,
    [templates, activeId],
  );
  useEffect(() => {
    if (active) setForm({
      name: active.name, subject: active.subject, body: active.body,
    });
    else setForm(null);
  }, [active]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!active || !form) return;
    setSaving(true);
    try {
      await api.patch(`/dunning/letter-templates/${active.id}`, form);
      toast.success("Vorlage gespeichert");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!active) return;
    if (!await window._domivusConfirm(`Systemvorlage "${active.name}" auf Auslieferungsstand zurücksetzen? Ihre Änderungen gehen verloren.`)) return;
    try {
      await api.post(`/dunning/letter-templates/${active.id}/reset`);
      toast.success("Vorlage zurückgesetzt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
    }
  };

  const remove = async () => {
    if (!active) return;
    const usage = (active.assigned_to || []).length;
    let force = false;
    let msg = `Vorlage "${active.name}" wirklich löschen?`;
    if (usage > 0) {
      msg += `\n\nDie Vorlage ist noch ${usage} Situation(en) zugewiesen. Wenn Sie fortfahren, werden diese Zuordnungen automatisch auf die System-Standard-Vorlage zurückgesetzt.`;
      force = true;
    }
    if (!await window._domivusConfirm(msg)) return;
    try {
      const url = force
        ? `/dunning/letter-templates/${active.id}?force=true`
        : `/dunning/letter-templates/${active.id}`;
      await api.delete(url);
      toast.success("Gelöscht");
      setActiveId(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.detail) toast.error(detail.detail);
      else toast.error(detail || "Löschen fehlgeschlagen");
    }
  };

  const duplicate = async () => {
    if (!active) return;
    try {
      const { data } = await api.post("/dunning/letter-templates", {
        name: `${active.name} (Kopie)`,
        subject: active.subject,
        body: active.body,
        kind: active.kind,
        scenario: active.scenario,
        level: active.level,
      });
      toast.success("Kopie angelegt");
      await load();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplizieren fehlgeschlagen");
    }
  };

  const setAssignment = async (kind, scenario, level, templateId) => {
    try {
      await api.put("/dunning/letter-templates/assign", {
        kind, scenario, level, template_id: templateId,
      });
      toast.success("Zuordnung aktualisiert");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zuordnung fehlgeschlagen");
    }
  };

  const insertPlaceholder = (tag) => {
    if (!form) return;
    setField("body", (form.body || "") + " " + tag);
  };

  const assignmentMap = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      m[`${a.kind}:${a.scenario}:${a.level ?? "-"}`] = a.template_id;
    }
    return m;
  }, [assignments]);

  const rentTemplatesByScenario = useMemo(() => {
    const g = {};
    for (const t of templates.filter((t) => t.kind === "rent")) {
      g[t.scenario] = g[t.scenario] || [];
      g[t.scenario].push(t);
    }
    return g;
  }, [templates]);

  const depositTemplates = useMemo(
    () => templates.filter((t) => t.kind === "deposit"),
    [templates],
  );

  return (
    <div className="space-y-4" data-testid="dunning-letter-templates-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Envelope size={24} weight="bold" /> Briefvorlagen — Zahlungsaufforderungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Situationsabhängige Vorlagen. Domivus wählt automatisch die passende Vorlage
            anhand der tatsächlichen Forderungslage. System-Vorlagen können bearbeitet und
            auf den Auslieferungsstand zurückgesetzt werden.
          </p>
        </div>
        <Button variant="outline" onClick={() => setCreating(true)} data-testid="new-template-btn">
          <Plus size={14} className="mr-1.5" /> Neue Vorlage
        </Button>
      </div>

      <Tabs defaultValue="assignment">
        <TabsList>
          <TabsTrigger value="assignment" data-testid="tab-assignment">Situationszuordnung</TabsTrigger>
          <TabsTrigger value="editor" data-testid="tab-editor">Vorlagen-Editor</TabsTrigger>
        </TabsList>

        {/* ---- Situationszuordnung ---- */}
        <TabsContent value="assignment" className="pt-3 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-900 flex items-start gap-2">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            Wählen Sie pro Miet-Situation und Stufe die Vorlage, die Domivus verwenden soll.
            Änderungen greifen sofort beim nächsten Zahlungsaufforderungs-Vorschlag.
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 w-1/3">Situation</th>
                  {situations.levels.map((lvl) => (
                    <th key={lvl.level} className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">
                      {lvl.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {situations.rent.map((s) => (
                  <tr key={s.scenario} className="border-t border-slate-100">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-slate-800">{s.label}</div>
                      <div className="text-[11px] text-slate-500">{s.description}</div>
                    </td>
                    {situations.levels.map((lvl) => {
                      const current = assignmentMap[`rent:${s.scenario}:${lvl.level}`] || "";
                      const options = rentTemplatesByScenario[s.scenario] || [];
                      return (
                        <td key={lvl.level} className="px-3 py-2 align-top">
                          <Select
                            value={current}
                            onValueChange={(v) => setAssignment("rent", s.scenario, lvl.level, v)}
                          >
                            <SelectTrigger className="w-full text-xs" data-testid={`assign-rent-${s.scenario}-l${lvl.level}`}>
                              <SelectValue placeholder="— nicht zugewiesen —" />
                            </SelectTrigger>
                            <SelectContent>
                              {options.length === 0 && (
                                <SelectItem value="__none" disabled>Keine Vorlage</SelectItem>
                              )}
                              {options.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.is_system ? "🔒 " : ""}{t.name}{t.level ? ` (Stufe ${t.level})` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-600">
              Kaution-Situationen
            </div>
            <table className="w-full text-sm">
              <tbody>
                {situations.deposit.map((s) => {
                  const current = assignmentMap[`deposit:${s.scenario}:-`] || "";
                  return (
                    <tr key={s.scenario} className="border-t border-slate-100">
                      <td className="px-3 py-2 align-top w-1/3">
                        <div className="font-medium text-slate-800">{s.label}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={current}
                          onValueChange={(v) => setAssignment("deposit", s.scenario, null, v)}
                        >
                          <SelectTrigger className="w-full text-xs" data-testid={`assign-deposit-${s.scenario}`}>
                            <SelectValue placeholder="— nicht zugewiesen —" />
                          </SelectTrigger>
                          <SelectContent>
                            {depositTemplates.filter((t) => t.scenario === s.scenario).map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.is_system ? "🔒 " : ""}{t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---- Vorlagen-Editor ---- */}
        <TabsContent value="editor" className="pt-3">
          <div className="grid grid-cols-12 gap-4">
            {/* Vorlagenliste */}
            <div className="col-span-4 bg-white border border-slate-200 rounded-md overflow-hidden max-h-[70vh] overflow-y-auto">
              {templates.length === 0 && (
                <div className="p-6 text-center text-slate-500 text-sm">
                  Keine Vorlagen vorhanden.
                </div>
              )}
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${
                    activeId === t.id ? "bg-emerald-50" : ""
                  }`}
                  data-testid={`tpl-list-${t.id}`}
                >
                  <div className="flex items-center gap-1.5 text-xs">
                    {t.is_system && <Lock size={11} className="text-slate-400" />}
                    <span className="font-medium text-slate-800">{t.name}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {t.kind === "rent" ? "Miete" : "Kaution"}
                    {" · "}{t.scenario}
                    {t.level ? ` · Stufe ${t.level}` : ""}
                  </div>
                  {(t.assigned_to || []).length > 0 && (
                    <div className="text-[10px] text-emerald-700 mt-0.5">
                      {t.assigned_to.length}× zugewiesen
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Editor */}
            <div className="col-span-8 bg-white border border-slate-200 rounded-md p-4 space-y-3">
              {!form ? (
                <div className="text-slate-500 text-sm p-6 text-center">
                  Vorlage links auswählen …
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    {active?.is_system ? (
                      <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                        <Lock size={10} className="mr-1" /> System-Vorlage
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        Eigene Vorlage
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {active?.kind === "rent" ? "Miete" : "Kaution"} · {active?.scenario}
                      {active?.level ? ` · Stufe ${active.level}` : ""}
                    </Badge>
                    <div className="flex-1" />
                    <Button variant="outline" size="sm" onClick={duplicate} data-testid="tpl-duplicate">
                      <Copy size={13} className="mr-1" /> Duplizieren
                    </Button>
                    {active?.is_system && (
                      <Button variant="outline" size="sm" onClick={reset} data-testid="tpl-reset">
                        <ArrowClockwise size={13} className="mr-1" /> Auf Standard zurücksetzen
                      </Button>
                    )}
                    {!active?.is_system && (
                      <Button
                        variant="outline" size="sm"
                        className="text-rose-600 hover:bg-rose-50"
                        onClick={remove}
                        data-testid="tpl-delete"
                      >
                        <Trash size={13} className="mr-1" /> Löschen
                      </Button>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Interne Bezeichnung</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      data-testid="tpl-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Betreff</Label>
                    <Input
                      value={form.subject}
                      onChange={(e) => setField("subject", e.target.value)}
                      data-testid="tpl-subject"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Body (HTML)</Label>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setShowPlaceholders((v) => !v)}
                        data-testid="tpl-placeholders-toggle"
                      >
                        {showPlaceholders ? "Platzhalter ausblenden" : "Platzhalter anzeigen"}
                      </Button>
                    </div>
                    <Textarea
                      rows={16}
                      value={form.body}
                      onChange={(e) => setField("body", e.target.value)}
                      className="font-mono text-xs"
                      data-testid="tpl-body"
                    />
                  </div>

                  {showPlaceholders && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-2 max-h-64 overflow-y-auto">
                      {placeholders.groups.map((g) => (
                        <div key={g.key}>
                          <div className="font-semibold text-slate-700 mb-1">{g.label}</div>
                          <div className="flex flex-wrap gap-1">
                            {g.items.map((it) => (
                              <button
                                key={it.tag}
                                type="button"
                                onClick={() => insertPlaceholder(it.tag)}
                                title={it.desc}
                                className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-[10px] font-mono hover:bg-emerald-50 hover:border-emerald-300"
                              >
                                {it.tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                    <Button
                      onClick={save} disabled={saving}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="tpl-save"
                    >
                      <FloppyDisk size={13} className="mr-1" />
                      {saving ? "Speichere …" : "Speichern"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <NewTemplateDialog
        open={creating}
        onClose={() => setCreating(false)}
        situations={situations}
        onCreated={async (id) => {
          await load();
          setActiveId(id);
          setCreating(false);
        }}
      />
    </div>
  );
}


function NewTemplateDialog({ open, onClose, situations, onCreated }) {
  const [form, setForm] = useState({
    name: "", subject: "", body: "<p>{{recipient.greeting}}</p>\n\n<p>…</p>\n\n<p>Mit freundlichen Grüßen<br>{{sender.name}}</p>",
    kind: "rent", scenario: "full_unpaid", level: 1,
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.post("/dunning/letter-templates", form);
      toast.success("Vorlage angelegt");
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-tpl-dialog">
        <DialogHeader>
          <DialogTitle>Neue Vorlage anlegen</DialogTitle>
          <DialogDescription>
            Erstellen Sie eine neue benutzerdefinierte Vorlage für eine Zahlungsaufforderungs-Situation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Bezeichnung</Label>
            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="new-tpl-name" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Typ</Label>
              <Select value={form.kind} onValueChange={(v) => setField("kind", v)}>
                <SelectTrigger data-testid="new-tpl-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">Miete</SelectItem>
                  <SelectItem value="deposit">Kaution</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Situation</Label>
              <Select value={form.scenario} onValueChange={(v) => setField("scenario", v)}>
                <SelectTrigger data-testid="new-tpl-scenario"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(form.kind === "rent" ? situations.rent : situations.deposit).map((s) => (
                    <SelectItem key={s.scenario} value={s.scenario}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.kind === "rent" && (
            <div>
              <Label className="text-xs">Stufe</Label>
              <Select value={String(form.level)} onValueChange={(v) => setField("level", Number(v))}>
                <SelectTrigger data-testid="new-tpl-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {situations.levels?.map((lvl) => (
                    <SelectItem key={lvl.level} value={String(lvl.level)}>{lvl.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Betreff</Label>
            <Input value={form.subject} onChange={(e) => setField("subject", e.target.value)} data-testid="new-tpl-subject" />
          </div>
          <div>
            <Label className="text-xs">Body (HTML)</Label>
            <Textarea rows={8} className="font-mono text-xs" value={form.body}
                      onChange={(e) => setField("body", e.target.value)} data-testid="new-tpl-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={save}
            className="bg-slate-900 hover:bg-slate-800"
            disabled={!form.name || !form.subject || !form.body}
            data-testid="new-tpl-save"
          >
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
